import { useEffect, useState } from "react";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { INTERESTS, SITE_TYPES } from "./data";
import { Eyebrow, MaskLines, Reveal } from "./motion";

const ENDPOINT = process.env.REACT_APP_CONTACT_ENDPOINT || `${process.env.REACT_APP_BACKEND_URL}/api/contact`;
const EMPTY = { name: "", email: "", company: "", phone: "", message: "", website: "" };

const Field = ({ id, label, ...rest }) => (
  <label className="block group">
    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 group-focus-within:text-cyan-300 transition-colors">{label}</span>
    <input id={id} data-testid={`contact-${id}-input`} className="mt-2 w-full bg-transparent border-b border-white/15 py-3 text-lg outline-none focus:border-cyan-300 transition-colors placeholder:text-slate-700" {...rest} />
  </label>
);

const Chip = ({ on, children, ...rest }) => (
  <button type="button" className={`rounded-full border px-4 py-2 text-sm transition-colors ${on ? "border-cyan-300 bg-cyan-300 text-void" : "border-white/15 text-slate-300 hover:border-white/40"}`} {...rest}>
    {children}
  </button>
);

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [site, setSite] = useState("");
  const [interests, setInterests] = useState([]);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  useEffect(() => {
    const on = (e) => setForm((f) => ({ ...f, message: e.detail }));
    window.addEventListener("lns:prefill", on);
    return () => window.removeEventListener("lns:prefill", on);
  }, []);
  const toggle = (i) => setInterests((a) => (a.includes(i) ? a.filter((x) => x !== i) : [...a, i]));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, site_type: site || null, interests }),
      });
      if (!res.ok) throw new Error(String(res.status));
      toast.success("Message received — Stephen will be in touch shortly.");
      setForm(EMPTY);
      setSite("");
      setInterests([]);
    } catch {
      toast.error("Couldn't send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-36 overflow-hidden" data-testid="contact-section">
      <div className="absolute -left-40 top-20 h-[500px] w-[500px] rounded-full bg-cyan-500/10 blur-[120px]" aria-hidden />
      <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <Eyebrow>Free consultation</Eyebrow>
          <h2 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[0.95]">
            <MaskLines lines={["Let's map", "your", <span className="font-serif-i font-normal text-cyan-300">network.</span>]} />
          </h2>
          <Reveal delay={0.1}>
            <p className="mt-8 text-slate-400 leading-relaxed max-w-md">A no-obligation conversation about your site, your current setup and what a modern network could look like. Tell me a little below and I'll come back to you directly.</p>
          </Reveal>
        </div>
        <Reveal className="lg:col-span-7" delay={0.1}>
          <form onSubmit={submit} className="chamfer border border-white/10 bg-[#0B0F17]/80 backdrop-blur-xl p-7 sm:p-10 space-y-9" data-testid="contact-form">
            {/* Honeypot: hidden from people, bots fill it in and get silently dropped by the enquiry Worker. */}
            <div aria-hidden="true" className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden">
              <label>
                Website
                <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
              </label>
            </div>
            <div className="grid sm:grid-cols-2 gap-8">
              <Field id="name" label="Name *" required value={form.name} onChange={set("name")} placeholder="Jane Smith" />
              <Field id="email" label="Email *" type="email" required value={form.email} onChange={set("email")} placeholder="jane@company.com" />
              <Field id="company" label="Company" value={form.company} onChange={set("company")} placeholder="Acme Ltd" />
              <Field id="phone" label="Phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="Optional" />
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-4">Site type</p>
              <div className="flex flex-wrap gap-2">
                {SITE_TYPES.map((s) => <Chip key={s} on={site === s} onClick={() => setSite(site === s ? "" : s)} data-testid={`contact-site-${s.toLowerCase().replace(/\s+/g, "-")}`}>{s}</Chip>)}
              </div>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-4">Interested in</p>
              <div className="flex flex-wrap gap-2">
                {INTERESTS.map((s) => <Chip key={s} on={interests.includes(s)} onClick={() => toggle(s)} data-testid={`contact-interest-${s.toLowerCase().replace(/\s+/g, "-")}`}>{s}</Chip>)}
              </div>
            </div>
            <label className="block group">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 group-focus-within:text-cyan-300 transition-colors">Tell me about your setup *</span>
              <textarea required rows={4} value={form.message} onChange={set("message")} data-testid="contact-message-input" placeholder="Two floors, 30 staff, Wi-Fi drops in the back office…" className="mt-2 w-full resize-none bg-transparent border-b border-white/15 py-3 text-lg outline-none focus:border-cyan-300 transition-colors placeholder:text-slate-700" />
            </label>
            <button type="submit" disabled={sending} data-testid="contact-submit-button" className="group inline-flex items-center gap-3 rounded-full bg-cyan-300 text-void pl-7 pr-2 py-2 font-semibold hover:bg-white transition-colors disabled:opacity-60">
              {sending ? "Sending…" : "Send message"}
              <span className="grid h-10 w-10 place-items-center rounded-full bg-void text-cyan-300 transition-transform duration-500 group-hover:rotate-45">
                {sending ? <Loader2 size={18} className="animate-spin" /> : <ArrowUpRight size={18} />}
              </span>
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
